import { useMyEvents } from 'modules/calendar/events/hooks/use-my-events'
import { useCommunities } from 'modules/commutinies/hooks/use-communities'
import { useCourseBundlesListAll } from 'modules/courses/bundles/hooks/use-course-bundlies-list-all'
import { coursesDependencies } from 'modules/courses/constants/courses-dependencies'
import { DigitalResourcesTypeEnum } from 'modules/digital-product/enums/digital-resources-type-enum'
import { useTagsAll } from 'modules/tags/hooks/use-tags-all'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useControlledSwr } from 'shared/hooks/use-controlled-swr'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import useUser from 'shared/hooks/use-user'
import useSWR from 'swr'

export interface BaseApiInterface {
  hasMore: boolean
  items: { id: number; name: string }[]
}

export interface ListingBaseDataInterface {
  id: number
  caption: string
}

export interface UseDigitalResourcesPropsInterface {
  resourceType?: DigitalResourcesTypeEnum
  courseId?: number | null
  courseBundleId?: number | null
  query?: string
}

export interface DigitalResourcesDataInterface {
  coursesData?: ListingBaseDataInterface[]
  courseBundlesData?: ListingBaseDataInterface[]
  plansData?: ListingBaseDataInterface[]
  tagsData?: ListingBaseDataInterface[]
  communitiesData?: ListingBaseDataInterface[]
  courseModulesData?: ListingBaseDataInterface[]
  courseBundleCoursesData?: ListingBaseDataInterface[]
  eventsData?: ListingBaseDataInterface[]
}

export const getData = (apiData?: BaseApiInterface): ListingBaseDataInterface[] | undefined =>
  apiData?.items.map(el => ({ id: el.id, caption: el.name }))

const getSimpleData = (
  apiData?: BaseApiInterface['items'],
): ListingBaseDataInterface[] | undefined => apiData?.map(el => ({ id: el.id, caption: el.name }))

export function useDigitalResources({
  resourceType,
  courseId,
  courseBundleId,
  query,
}: UseDigitalResourcesPropsInterface): DigitalResourcesDataInterface {
  const { user } = useUser()
  const { fetcher } = useFetch<RequestMethodsEnum.get, BaseApiInterface>({
    method: RequestMethodsEnum.get,
  })

  const { fetcher: simpleFetcher } = useFetch<RequestMethodsEnum.get, BaseApiInterface['items']>({
    method: RequestMethodsEnum.get,
  })

  const { data: coursesData } = useControlledSwr(() => {
    if (user && resourceType === DigitalResourcesTypeEnum.membership_course) {
      return [
        `/api/dashboard/customer/membership/courses/list-all?active=true${
          query ? `&${query}` : ''
        }`,
        RequestModuleEnum.MembershipCourse,
      ]
    }
    return null
  }, fetcher)

  const { data: courseModulesData } = useControlledSwr(() => {
    if (user && resourceType === DigitalResourcesTypeEnum.membership_course && courseId) {
      return [
        `/api/dashboard/customer/membership/courses/${courseId}/modules/list-all`,
        RequestModuleEnum.MembershipCourseCurriculum,
      ]
    }
    return null
  }, simpleFetcher)

  const { courseBundlesListAll } = useCourseBundlesListAll(
    resourceType === DigitalResourcesTypeEnum.membership_course_bundle,
  )

  const { data: courseBundleCoursesData } = useControlledSwr(() => {
    if (
      user &&
      resourceType === DigitalResourcesTypeEnum.membership_course_bundle &&
      courseBundleId
    ) {
      return [
        `/api/dashboard/customer/membership/course-bundles/${courseBundleId}/courses/list-all`,
        ...coursesDependencies,
      ]
    }
    return null
  }, simpleFetcher)

  const { data: plansData } = useSWR(() => {
    if (user && resourceType === DigitalResourcesTypeEnum.systemeio_plan) {
      return '/api/dashboard/customer/user-plans/list/auto-upgrade'
    }
    return null
  }, fetcher)

  const { tags: tagsData } = useTagsAll(resourceType === DigitalResourcesTypeEnum.systemeio_tag)

  const { data: communitiesData } = useCommunities(
    resourceType === DigitalResourcesTypeEnum.community_access,
  )

  const { myEvents: eventsData } = useMyEvents(
    resourceType === DigitalResourcesTypeEnum.booking_event,
  )

  return {
    coursesData: getData(coursesData),
    courseBundlesData: getData(courseBundlesListAll),
    plansData: getData(plansData),
    tagsData: getData(tagsData),
    communitiesData: getSimpleData(communitiesData),
    courseModulesData: getSimpleData(courseModulesData),
    courseBundleCoursesData: getSimpleData(courseBundleCoursesData),
    eventsData: getSimpleData(eventsData),
  }
}
